/**
 * MediaVariantCard.tsx
 *
 * Generic media-card renderer for unified section rails.
 * Supports hover-preview, active player state, and status badges.
 */

import Image from "next/image";
import type React from "react";
import type { UnifiedMediaCardProps } from "../../../types/livelanding.types";
import { thumbnailLoader } from "../../../utils/image";
import { LiveBadgeIcon, PlayIcon } from "../../Icons/MediaIcons";
import { ShadowSpinner } from "../../Loader/ShadowSpinner";
import { VideoPlayer } from "../../VideoPlayer/VideoPlayer";
import { PipOverlayLabel } from "../PipOverlayLabel";
import { CardHoverPreview, getAnimatedPosterUrl } from "./CardHoverPreview";
import { formatViews, getOverlayOpacityClass } from "./cardDisplayUtils";

/**
 * Renders live/replay status badge for media card header.
 */
function renderMediaStatusBadge({
  endedAt,
  videoEndedAt,
  effectiveIsLive,
  endedAtTimeString,
  status,
}: {
  endedAt: string | null | undefined;
  videoEndedAt: string | null | undefined;
  effectiveIsLive: boolean;
  endedAtTimeString: string;
  status: string | null | undefined;
}) {
  const hasEndedAt = Boolean(endedAt) || Boolean(videoEndedAt);
  if (hasEndedAt) {
    if (effectiveIsLive) return null;
    return (
      <span className="font-rubik rounded-[2px] bg-[#1A1A1AB2] !px-[5px] !py-[2px] !text-[10px] font-medium text-white sm:!px-[4px] sm:!py-[1px] sm:!text-[10px] md:!px-[6px] md:!py-[1px] md:!text-[12px] xl:!px-[5px] xl:!py-[1px] xl:!text-[14px]">
        {endedAtTimeString}
      </span>
    );
  }

  if (status === "active") {
    return (
      <span className="mr-[11px] flex items-center gap-1 rounded-sm bg-[#ff0000] !px-1.5 py-0.5 text-[10px] font-medium text-white uppercase md:px-0 md:!text-[14px]">
        <LiveBadgeIcon />
        LIVE
      </span>
    );
  }

  return null;
}

type MediaVariantCardProps = {
  encodedId?: string;
  size?: string;
  textColor?: "light" | "dark";
  thumbnail: string;
  video: UnifiedMediaCardProps["video"];
  ended_at?: string | null;
  effectiveIsLive: boolean;
  endedAtTimeString: string;
  viewsCount: number;
  isPlayerActive: boolean;
  isLoading: boolean;
  isCardHovered: boolean;
  isPreviewReady: boolean;
  isPreviewLoading: boolean;
  pipThumbnailOverlayHidden: boolean;
  previewVideoRef: React.RefObject<HTMLVideoElement | null>;
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  handleClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  onPreviewReady: () => void;
};

/**
 * Renders unified media variant card used by section rails.
 */
export function MediaVariantCard({
  encodedId,
  size,
  textColor = "dark",
  thumbnail,
  video,
  ended_at,
  effectiveIsLive,
  endedAtTimeString,
  viewsCount,
  isPlayerActive,
  isLoading,
  isCardHovered,
  isPreviewReady,
  isPreviewLoading,
  pipThumbnailOverlayHidden,
  previewVideoRef,
  handleMouseEnter,
  handleMouseLeave,
  handleClick,
  handleKeyDown,
  onPreviewReady,
}: Readonly<MediaVariantCardProps>) {
  const animatedPoster = getAnimatedPosterUrl(video);
  const shouldRevealPreview = isCardHovered && isPreviewReady && !pipThumbnailOverlayHidden;
  const overlayOpacityClass = getOverlayOpacityClass(
    pipThumbnailOverlayHidden,
    shouldRevealPreview
  );
  const showHoverPreview = isCardHovered && !pipThumbnailOverlayHidden;

  return (
    <div
      id={encodedId}
      className={`!max-w-[112px] flex-[0_0_250px] sm:!max-w-[140px] md:!max-w-[190px] md:flex-[0_0_300px] lg:!max-w-[200px] xl:!max-w-[210px] ${size ?? ""}`}
    >
      <button
        type="button"
        className="font-inherit group z-[1] h-full w-full cursor-pointer overflow-hidden border-0 bg-transparent p-0 text-left"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <div className="relative aspect-[9/15.8] w-full overflow-hidden rounded-lg bg-black sm:aspect-[9/16.3]">
          <div className="absolute top-0 right-0 left-0 z-[3] flex items-center justify-between bg-gradient-to-b from-[rgba(0,0,0,0.6)] to-transparent p-1 sm:p-1 lg:p-2">
            {renderMediaStatusBadge({
              endedAt: ended_at,
              videoEndedAt: video?.ended_at,
              effectiveIsLive,
              endedAtTimeString,
              status: video?.status,
            })}

            {viewsCount > 0 && (
              <span className="flex items-center gap-1 rounded-[2px] bg-[#1A1A1AB2] px-[6px] py-[2px] text-[10px] font-medium text-white md:text-[12px]">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5C7 5 3 9 2 12c1 3 5 7 10 7s9-4 10-7c-1-3-5-7-10-7Zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                    fill="white"
                  />
                </svg>
                {formatViews(viewsCount)}
              </span>
            )}
          </div>
          <div className="absolute top-0 left-0 h-full w-full">
            <CardHoverPreview
              isActive={showHoverPreview}
              animatedPoster={animatedPoster}
              trailerUrl={video.trailer?.file_url ?? undefined}
              captionsUrl={video.trailer?.captions_url ?? undefined}
              videoRef={previewVideoRef}
              alt={video.event_name ? `${video.event_name} preview` : "Video preview"}
              videoClassName="absolute top-0 left-0 z-[1] h-full w-full object-cover"
              onReady={onPreviewReady}
            />
            {isPlayerActive ? (
              <VideoPlayer
                className="h-full w-full"
                videoId={video.trailer?.encoded_id ?? ""}
                autoplay={false}
                maxVideos={1}
                playerCaptions={true}
                productCard={false}
              />
            ) : null}
          </div>
          {(isLoading || isPreviewLoading) && (
            <div className="pointer-events-none absolute inset-0 z-[10] flex items-center justify-center bg-black/50">
              <ShadowSpinner ariaLabel={isLoading ? "Loading video" : "Loading preview"} />
            </div>
          )}
          <div
            className={`absolute inset-0 transition-opacity duration-300 ease-in-out ${overlayOpacityClass}`}
            style={{ zIndex: 2 }}
          >
            <div className="absolute inset-0 bg-black/20">
              <Image
                src={thumbnail}
                alt={video.event_name || "Video"}
                fill
                loader={thumbnailLoader}
                className="object-cover"
                sizes="(max-width: 420px) 112px, (max-width: 768px) 140px, (max-width: 1024px) 190px, 210px"
                quality={70}
                priority={false}
              />
            </div>
            <div
              className={`absolute inset-0 m-auto flex h-[42px] w-[42px] items-center justify-center rounded-full bg-black/40 transition-all duration-300 ease-in-out max-[768px]:!h-[28px] max-[768px]:!w-[28px]`}
            >
              <PlayIcon className="ml-[3px] h-[30px] w-[30px]" />
            </div>
          </div>

          {pipThumbnailOverlayHidden && <PipOverlayLabel />}
        </div>
      </button>
      <div>
        <h3
          className={`line-clamp-2 overflow-hidden pt-[6px] font-['Rubik'] text-[16px] leading-[1.4] font-normal ${
            textColor === "light" ? "text-white" : "text-[#1A1A1A]"
          } max-[1200px]:text-[16px] max-[768px]:text-[14px] max-[420px]:text-[12px]`}
        >
          {video.event_name}
        </h3>
        {video.event_description && (
          <div
            className={`-ml-[2px] inline-block px-[2px] py-[2px] font-[Rubik] text-[14px] font-normal ${
              textColor === "light" ? "text-[#CCCCCC]" : "text-[#999999]"
            } max-[420px]:text-[12px] md:text-[14px] xl:text-[14px]`}
          >
            {video.event_description}
          </div>
        )}
      </div>
      {encodedId && <span style={{ display: "none" }}>{encodedId}</span>}
    </div>
  );
}
